import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, CheckCheck, ChevronDown, ChevronUp, Send, Facebook, Mail } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const CATEGORY_STYLES = {
  "Mechanical Work": "text-crimson border-crimson/40 bg-crimson/10",
  "Diagnostics": "text-sky-400 border-sky-400/40 bg-sky-400/10",
  "General": "text-slate-400 border-[#263342] bg-transparent",
};

const CATEGORY_FILTERS = [
  { id: "jobs", label: "My Job Listings" },
  { id: "Mechanical Work", label: "Mechanical Work" },
  { id: "Diagnostics", label: "Diagnostics" },
  { id: "all", label: "All Messages" },
];

const SOURCE_FILTERS = [
  { id: "all", label: "All Sources" },
  { id: "facebook", label: "Facebook" },
  { id: "email", label: "Email" },
];

export default function InboxPanel() {
  const [messages, setMessages] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("jobs");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fbConfigured, setFbConfigured] = useState(null);
  const [emailConfigured, setEmailConfigured] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const catParam = categoryFilter === "all" ? "" : `?category=${encodeURIComponent(categoryFilter)}`;
      const [fbStatus, emailStatus, fbMsgs, emailMsgs] = await Promise.all([
        api.get("/fb-config/status"),
        api.get("/email-config/status"),
        api.get(`/fb-messages${catParam}`),
        api.get(`/email-messages${catParam}`),
      ]);
      setFbConfigured(fbStatus.data.configured);
      setEmailConfigured(emailStatus.data.configured);
      setMessages([
        ...fbMsgs.data.map((m) => ({ ...m, source: "facebook" })),
        ...emailMsgs.data.map((m) => ({ ...m, source: "email" })),
      ]);
    } catch (err) {
      toast.error("Could not load inbox", { description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  }, [categoryFilter]);

  useEffect(() => { load(); }, [load]);

  const conversations = useMemo(() => {
    const map = new Map();
    for (const m of messages) {
      const key = `${m.source}:${m.sender_id}`;
      if (!map.has(key)) map.set(key, { key, sender_id: m.sender_id, sender_name: m.sender_name || (m.source === "email" ? m.sender_id : "Facebook user"), source: m.source, msgs: [] });
      map.get(key).msgs.push(m);
    }
    return [...map.values()]
      .map((c) => ({
        ...c,
        latest: [...c.msgs].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0],
        unread: c.msgs.filter((m) => !m.read && m.direction !== "out").length,
        category: c.msgs.find((m) => m.category !== "General")?.category || "General",
        subject: c.msgs.find((m) => m.subject)?.subject || "",
      }))
      .filter((c) => sourceFilter === "all" || c.source === sourceFilter)
      .sort((a, b) => new Date(b.latest.timestamp) - new Date(a.latest.timestamp));
  }, [messages, sourceFilter]);

  const markRead = async (c) => {
    try {
      const base = c.source === "email" ? "/email-messages" : "/fb-messages";
      await api.patch(`${base}/read/${encodeURIComponent(c.sender_id)}`);
      setMessages((list) => list.map((m) => (m.source === c.source && m.sender_id === c.sender_id ? { ...m, read: true } : m)));
    } catch (err) {
      toast.error("Could not mark as read", { description: formatApiError(err) });
    }
  };

  const sendReply = async (c) => {
    const text = replyText.trim();
    if (!text) return;
    setSending(true);
    try {
      if (c.source === "email") {
        await api.post("/email-messages/reply", { sender_id: c.sender_id, subject: c.subject, text });
      } else {
        await api.post("/fb-messages/reply", { sender_id: c.sender_id, text });
      }
      setReplyText("");
      toast.success(c.source === "email" ? "Reply emailed to customer" : "Reply sent to Facebook");
      load();
    } catch (err) {
      toast.error("Reply failed", { description: formatApiError(err) });
    } finally {
      setSending(false);
    }
  };

  const replyEnabled = (c) => (c.source === "email" ? true : fbConfigured);

  return (
    <div data-testid="inbox-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display font-bold uppercase tracking-tight text-2xl text-slate-100">Inbox</h2>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-1">
            Facebook + Email — auto-sorted into your two job listings — visible only after login
          </p>
        </div>
        <button data-testid="inbox-refresh-button" onClick={load} className="text-slate-400 hover:text-crimson transition-colors" aria-label="Refresh inbox">
          <RefreshCw size={18} />
        </button>
      </div>

      {(fbConfigured === false || emailConfigured === false) && (
        <div className="mb-6 space-y-3" data-testid="inbox-setup-banners">
          {fbConfigured === false && (
            <div className="bg-amber-400/10 border border-amber-400/40 p-5" data-testid="fb-not-configured-banner">
              <p className="font-display font-bold uppercase tracking-wide text-amber-400">Facebook not connected yet</p>
              <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                Facebook messages will appear here once your Page is linked through a free Meta developer app — ask me for the 10-minute walkthrough.
              </p>
            </div>
          )}
          {emailConfigured === false && (
            <div className="bg-amber-400/10 border border-amber-400/40 p-5" data-testid="email-not-configured-banner">
              <p className="font-display font-bold uppercase tracking-wide text-amber-400">Email receiving not connected yet</p>
              <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                Emails will appear here once a receiving address is set up (takes one DNS record or a free instant address) — ask me and I'll guide you. Your existing Gmail keeps working either way.
              </p>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-3" data-testid="inbox-category-filters">
        {CATEGORY_FILTERS.map((f) => (
          <button
            key={f.id}
            data-testid={`inbox-filter-${f.id.toLowerCase().replace(/[^a-z]/g, "-")}`}
            onClick={() => setCategoryFilter(f.id)}
            className={`font-mono text-xs uppercase tracking-[0.15em] px-4 py-2.5 border transition-colors ${
              categoryFilter === f.id ? "border-crimson bg-crimson/10 text-crimson" : "border-[#263342] text-slate-400 hover:text-slate-200"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-6" data-testid="inbox-source-filters">
        {SOURCE_FILTERS.map((f) => (
          <button
            key={f.id}
            data-testid={`inbox-source-${f.id}`}
            onClick={() => setSourceFilter(f.id)}
            className={`font-mono text-[10px] uppercase tracking-[0.15em] px-3 py-2 border transition-colors ${
              sourceFilter === f.id ? "border-slate-300 bg-slate-300/10 text-slate-100" : "border-[#263342] text-slate-500 hover:text-slate-300"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="p-10 text-center font-mono text-sm text-slate-500" data-testid="inbox-loading">Loading messages...</p>
      ) : conversations.length === 0 ? (
        <div className="bg-panel border border-[#263342] p-10 text-center" data-testid="inbox-empty-state">
          <p className="font-mono text-sm text-slate-500">No messages here yet. New Facebook messages and emails will appear automatically once connected.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="inbox-conversation-list">
          {conversations.map((c, i) => (
            <div key={c.key} className="bg-panel border border-[#263342]" data-testid={`inbox-conversation-${i}`}>
              <button
                className="w-full flex flex-wrap items-center gap-3 px-5 py-4 text-left hover:bg-[#1E2733]/60 transition-colors"
                onClick={() => { setExpanded(expanded === c.key ? null : c.key); setReplyText(""); }}
                data-testid={`inbox-conversation-toggle-${i}`}
              >
                <span className="w-9 h-9 shrink-0 bg-crimson/15 border border-crimson/40 flex items-center justify-center text-crimson">
                  {c.source === "email" ? <Mail size={16} /> : <Facebook size={16} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className={`block font-semibold text-sm ${c.unread ? "text-slate-100" : "text-slate-300"}`}>
                    {c.sender_name}
                    {c.unread > 0 && <span className="ml-2 bg-crimson text-white font-mono text-[10px] px-2 py-0.5">{c.unread} new</span>}
                  </span>
                  <span className="block text-xs text-slate-500 truncate mt-0.5">
                    {c.subject && <span className="text-slate-400">{c.subject} — </span>}
                    {c.latest.direction === "out" ? "You: " : ""}{c.latest.text}
                  </span>
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500 border border-[#263342] px-2.5 py-1">
                  {c.source === "email" ? "Email" : "Facebook"}
                </span>
                <span className={`font-mono text-[10px] uppercase tracking-[0.15em] border px-2.5 py-1 ${CATEGORY_STYLES[c.category]}`}>
                  {c.category}
                </span>
                <span className="font-mono text-[10px] text-slate-500 whitespace-nowrap">
                  {new Date(c.latest.timestamp).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}{" "}
                  {new Date(c.latest.timestamp).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                </span>
                {expanded === c.key ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
              </button>
              {expanded === c.key && (
                <div className="border-t border-[#263342]/60 px-5 py-4 bg-[#0D1117]" data-testid={`inbox-thread-${i}`}>
                  <div className="space-y-3 max-h-72 overflow-y-auto">
                    {[...c.msgs].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)).map((m) => (
                      <div
                        key={m.id}
                        className={`max-w-[80%] px-4 py-3 border ${
                          m.direction === "out" ? "ml-auto bg-crimson/10 border-crimson/40" : "bg-panel border-[#263342]"
                        }`}
                      >
                        {m.subject && m.direction !== "out" && <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400 mb-1">{m.subject}</p>}
                        <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">{m.text}</p>
                        <p className="mt-1.5 font-mono text-[10px] text-slate-500 flex items-center gap-3">
                          {m.direction === "out" && <span className="text-crimson uppercase tracking-[0.1em]">You</span>}
                          {new Date(m.timestamp).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                          {m.direction !== "out" && (
                            <span className={`uppercase tracking-[0.1em] ${m.category === "Diagnostics" ? "text-sky-400" : m.category === "Mechanical Work" ? "text-crimson" : ""}`}>{m.category}</span>
                          )}
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex gap-2">
                    <input
                      data-testid={`inbox-reply-input-${i}`}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") sendReply(c); }}
                      placeholder={
                        c.source === "email"
                          ? "Type a reply — sent as an email..."
                          : fbConfigured
                            ? "Type a reply to send via Facebook..."
                            : "Connect Facebook to enable replies"
                      }
                      disabled={!replyEnabled(c)}
                      className="flex-1 bg-panel border border-[#263342] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-crimson transition-colors disabled:opacity-50"
                    />
                    <button
                      data-testid={`inbox-reply-send-${i}`}
                      onClick={() => sendReply(c)}
                      disabled={!replyEnabled(c) || sending || !replyText.trim()}
                      className="flex items-center gap-2 bg-crimson text-white font-display font-semibold uppercase tracking-widest text-sm px-5 hover:-translate-y-0.5 transition-transform disabled:opacity-50 disabled:hover:translate-y-0"
                    >
                      <Send size={14} /> Send
                    </button>
                  </div>
                  {c.unread > 0 && (
                    <button
                      data-testid={`inbox-mark-read-${i}`}
                      onClick={() => markRead(c)}
                      className="mt-3 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] border border-[#263342] px-4 py-2.5 text-slate-300 hover:border-crimson hover:text-crimson transition-colors"
                    >
                      <CheckCheck size={14} /> Mark as read
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
